// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import { Card, Description, InlineAlert, LgPadding, LgSpacing } from '@alto/design-system';
import {
  DeliveryLogistics,
  useCheckoutValidationErrors,
  useGetDeliveryMethodByName,
  useOrderBundlingOptions,
} from '@alto/features';
import React, { useEffect } from 'react';
import { updateOrder } from '~shared/actions/checkout';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { getOrder } from '~shared/features/checkout/selectors/getOrder';
import { getShipmentPaymentMethodsByShipmentID } from '~shared/features/payments/selectors/getShipmentPaymentMethods';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { EditCartButton } from './EditCartButton';
import { MedSummaryList } from './MedSummaryList';

export const OrderBundlingCheckoutDeliverySummary = () => {
  const dispatch = useDispatchShared();
  const order = useSelectorShared(getOrder);
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);
  const { cartItems } = useCartData();
  const { hasValidationErrors } = useCheckoutValidationErrors();
  const { deliveryMethod } = useGetDeliveryMethodByName('courier');

  const { earliestUpcomingShipment, isPending } = useOrderBundlingOptions({
    address_id: order.address_id,
    origin: isEditingOrder ? 'edit_order' : 'checkout',
  });

  // payment method selector is hidden when payment method is not required and order bundling
  // for an existing shipment with split payments, set payment method amounts so bundled order
  // passes BE validation for split payment amounts
  const earliestUpcomingShipmentPaymentMethods = useSelectorShared((state) =>
    getShipmentPaymentMethodsByShipmentID(state, { shipmentID: earliestUpcomingShipment?.id }),
  );
  const paymentMethod = earliestUpcomingShipmentPaymentMethods.find(
    (spm) => spm.payment_method_id === order.payment_method_id,
  );
  const payment_method_amount = paymentMethod?.amount;
  const secondPaymentMethod = earliestUpcomingShipmentPaymentMethods.find(
    (spm) => spm.payment_method_id === order.second_payment_method_id,
  );
  const second_payment_method_amount = secondPaymentMethod?.amount;

  useEffect(() => {
    if (!payment_method_amount && !second_payment_method_amount) return;
    dispatch(updateOrder({ payment_method_amount, second_payment_method_amount }));
  }, [dispatch, payment_method_amount, second_payment_method_amount]);

  if (isPending || !earliestUpcomingShipment) {
    return null;
  }

  const upcomingBundlePrescriptionIDs = earliestUpcomingShipment.prescriptions.map((rx) => rx.id);

  return (
    <Card title="Updated order summary">
      <div>
        {!hasValidationErrors ? (
          <DeliveryLogistics
            date={order.date}
            homeToSignConfirmation={order.home_to_sign_confirmation}
            method={deliveryMethod}
            status="upcoming"
            deliverAfter={order.deliver_after}
            deliverBefore={order.deliver_before}
          />
        ) : (
          <LgPadding>
            <InlineAlert type="warning">
              <Description>Please complete your order to view your order details.</Description>
            </InlineAlert>
          </LgPadding>
        )}
        <LgPadding
          topPadding={SPACING.STATIC.NONE}
          bottomPadding={SPACING.STATIC.NONE}
        >
          <MedSummaryList
            cartItems={cartItems}
            includeSharpsContainer={order.include_sharps_container || false}
            upcomingBundlePrescriptionIDs={upcomingBundlePrescriptionIDs}
            earliestUpcomingShipmentID={earliestUpcomingShipment.id}
          />
          <EditCartButton />
          <LgSpacing />
        </LgPadding>
      </div>
    </Card>
  );
};
